/**
 * Reactive `location.pathname` — SPA-aware and ROUTER-AGNOSTIC, app-lifetime singleton.
 *
 * Why this exists: several pieces resolve the per-tab active account from the URL
 * `/u/<n>` prefix and must react to in-app navigation — but they run OUTSIDE the
 * `<RouterProvider>` tree (the PowerSync provider and `useSession().userId` are
 * mounted above it / consumed by `theme-provider`). The router's `useLocation()`
 * hook throws there ("Cannot read properties of null"), and importing the
 * `@/router` singleton creates a massive import cycle. So we observe the browser
 * History API directly: `popstate` covers back/forward and a one-time monkeypatch
 * of `pushState`/`replaceState` emits an event for SPA navigations (which never
 * fire `popstate`). TanStack Router drives `window.history` under the hood, so
 * this catches every navigation with zero router coupling.
 *
 * It's an app-lifetime singleton: one signal, listeners attached once and never
 * removed (they live as long as the page), so any module can read it reactively
 * without needing its own reactive-cleanup scope.
 */

import { createSignal } from "solid-js";

const LOCATION_CHANGE_EVENT = "biblestrong:locationchange";

let historyPatched = false;

/** Monkeypatch history once so SPA pushState/replaceState emit an observable event. */
function ensureHistoryPatched(): void {
  if (historyPatched || globalThis.history === undefined) {
    return;
  }
  historyPatched = true;
  for (const method of ["pushState", "replaceState"] as const) {
    const original = globalThis.history[method].bind(globalThis.history);
    globalThis.history[method] = (...args: Parameters<History["pushState"]>) => {
      const result = original(...args);
      globalThis.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
      return result;
    };
  }
}

let pathnameAccessor: (() => string) | null = null;

/**
 * Reactive accessor of the current `location.pathname`, updating on every
 * navigation (SPA push/replace + browser back/forward). App-lifetime singleton —
 * safe to call from anywhere (inside or outside the router tree).
 */
export function activePathname(): string {
  if (!pathnameAccessor) {
    ensureHistoryPatched();
    const initial = globalThis.location?.pathname ?? "/";
    const [pathname, setPathname] = createSignal<string>(initial);
    const update = () => {
      setPathname(globalThis.location.pathname);
    };
    if (typeof globalThis.addEventListener === "function") {
      globalThis.addEventListener("popstate", update);
      globalThis.addEventListener(LOCATION_CHANGE_EVENT, update);
    }
    pathnameAccessor = pathname;
  }
  return pathnameAccessor();
}
